import React, { useState } from "react";
import {
  Table,
  Button,
  Form,
  Input,
  Tag,
  Popconfirm,
  message,
  Empty,
  Tooltip,
  Drawer,
  Select,
  Switch,
  Spin,
} from "antd";
import {
  EditOutlined,
  SearchOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import { useStoreService } from "../../Services";
import {
  PageHeader,
  tablePagination,
  DRAWER_FORM_WIDTH,
  drawerTitle,
  drawerFooter,
} from "../../components";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY, ROUTE } from "../../Constant";
import { Store } from "../../Types";
import { useSectionAccess } from "../../Services";
import { AdminAction } from "../../Types";
import { useNavigate } from "react-router-dom";

export const StoresPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);

  // Drawer States (Add/Edit)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form] = Form.useForm();
  const { isAllowed } = useSectionAccess("admin");

  // Services
  const {
    storesQuery,
    createStoreMutation,
    updateStoreMutation,
    deleteStoreMutation,
  } = useStoreService({
    fetchStoresParams: {
      page,
      limit: PAGE_SIZE,
      search,
    },
    fetchStoresEnabled: true,
    fetchActiveStoresEnabled: false,
  });

  const stores = storesQuery.data;
  const total = (storesQuery as any).data?.meta?.total || 0;
  const isLoading = storesQuery.isLoading;

  const openDrawer = (store?: Store, creating = false) => {
    setIsCreating(creating);
    if (store && !creating) {
      setEditingStore(store);
      form.setFieldsValue({
        name: store.name,
        main_store: store.main_store === 1,
        type: store.type || "shop",
        latitude: store.latitude,
        longitude: store.longitude,
        status: store.status,
      });
    } else {
      setEditingStore(null);
      form.resetFields();
      form.setFieldsValue({
        type: "shop",
        status: "Active",
        main_store: false,
      });
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingStore(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        main_store: values.main_store ? 1 : 0,
      };

      if (isCreating) {
        createStoreMutation.mutate(payload, {
          onSuccess: (res) => {
            if (res?.success) {
              message.success("Store created successfully.");
              closeDrawer();
            } else {
              message.error(res?.message || "Failed to create store.");
            }
          },
          onError: (err: any) => {
            message.error(err?.message || "Failed to create store.");
          },
        });
      } else if (editingStore) {
        updateStoreMutation.mutate(
          { id: editingStore.id, ...payload },
          {
            onSuccess: (res) => {
              if (res?.success) {
                message.success("Store updated successfully.");
                closeDrawer();
              } else {
                message.error(res?.message || "Failed to update store.");
              }
            },
            onError: (err: any) => {
              message.error(err?.message || "Failed to update store.");
            },
          }
        );
      }
    } catch (error) {
      message.error("Please correct the form errors.");
    }
  };

  const handleDelete = (id: any) => {
    deleteStoreMutation.mutate(id, {
      onSuccess: (res) => {
        if (res?.success) {
          message.success("Store deleted successfully.");
        } else {
          message.error(res?.message || "Failed to delete store.");
        }
      },
      onError: (err: any) => {
        message.error(err?.message || "Failed to delete store.");
      },
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setPage(1);
  };

  const openMap = (store: Store) => {
    navigate(`../${ROUTE.store_live_tracking}/${store.id}`);
  };

  const columns = [
    {
      title: "Store Name",
      dataIndex: "name",
      key: "name",
      render: (name: string, rec: Store) => (
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-800 text-sm">{name}</span>
          {rec.main_store === 1 && (
            <Tag color="purple" className="font-semibold rounded-md text-xs">
              Main Store
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type: string) => {
        const isVehicle = type === "vehicle";
        return (
          <Tag
            color={isVehicle ? "orange" : "cyan"}
            className="font-semibold rounded-md text-xs"
          >
            {isVehicle ? "🚚 Vehicle" : "🏢 Shop"}
          </Tag>
        );
      },
    },
    {
      title: "GPS Location",
      key: "location",
      render: (_: any, rec: Store) => {
        if (rec.type === "vehicle") {
          return rec.latitude && rec.longitude ? (
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-gray-500">
                {Number(rec.latitude).toFixed(4)}, {Number(rec.longitude).toFixed(4)}
              </span>
              <Button
                type="link"
                size="small"
                icon={<EnvironmentOutlined />}
                onClick={() => openMap(rec)}
                className="text-xs text-orange-500 hover:text-orange-600 p-0"
              >
                Track Map
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 italic">No GPS Data</span>
              <Button
                type="link"
                size="small"
                icon={<EnvironmentOutlined />}
                onClick={() => openMap(rec)}
                className="text-xs text-gray-400 p-0"
              >
                Initialize
              </Button>
            </div>
          );
        }
        return <span className="text-xs text-gray-300">—</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={status === "Active" ? "success" : "default"}>{status}</Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (val: string) => (
        <span className="text-xs text-gray-400">
          {new Date(val).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, rec: Store) => (
        <div className="flex items-center gap-1">
          {isAllowed(AdminAction.ManageStores) && (
            <Tooltip title="Edit Store">
              <Button
                type="text"
                icon={<EditOutlined />}
                size="small"
                onClick={() => openDrawer(rec, false)}
              />
            </Tooltip>
          )}
          {rec.main_store !== 1 && isAllowed(AdminAction.ManageStores) && (
            <Popconfirm
              title="Delete Store?"
              description="Are you sure you want to delete this store? This action cannot be undone."
              onConfirm={() => handleDelete(rec.id)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete Store">
                <Button type="text" icon={<DeleteOutlined />} size="small" danger />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Store Registry"
        subText={`Manage store registers and vehicles.`}
        handleButtons={[
          {
            title: "Refresh",
            icon: ReloadOutlined,
            onClick: () => storesQuery.refetch(),
          },
        ]}
        searchBar={
          <Input
            placeholder="Search by store name…"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={handleSearchChange}
            allowClear
            onClear={() => {
              setSearchInput("");
              setPage(1);
            }}
            style={{ width: 260 }}
          />
        }
      />

      {/* Main Table */}
      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <Table
          dataSource={Array.isArray(stores) ? stores : (stores as any)?.stores || []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            itemName: "stores",
          })}
          scroll={{ x: 800 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No stores found"
              />
            ),
          }}
        />
      </div>

      {/* Store Create/Edit Drawer */}
      <Drawer
        title={drawerTitle(
          isCreating ? "Register New Store" : "Edit Store Registry",
        )}
        open={drawerOpen}
        onClose={closeDrawer}
        width={DRAWER_FORM_WIDTH}
        footer={drawerFooter({
          actions: [
            { label: "Cancel", onClick: closeDrawer },
            {
              label: isCreating ? "Create Store" : "Save Changes",
              type: "primary",
              loading: createStoreMutation.isPending || updateStoreMutation.isPending,
              onClick: handleSubmit,
            },
          ],
        })}
      >
        <Spin spinning={createStoreMutation.isPending || updateStoreMutation.isPending}>
          <Form form={form} layout="vertical" initialValues={{ type: "shop", status: "Active" }}>
            <Form.Item
              name="name"
              label="Store Name"
              rules={[{ required: true, message: "Please input store name" }]}
            >
              <Input placeholder="Enter store name" />
            </Form.Item>

            <Form.Item name="type" label="Store Type">
              <Select
                onChange={() => form.setFieldsValue({ latitude: undefined, longitude: undefined })}
                options={[
                  { value: "shop", label: "🏢 Retail Shop / Warehouse" },
                  { value: "vehicle", label: "🚚 Mobile Truck / Vehicle" },
                ]}
              />
            </Form.Item>

            {/* Conditional location input if Type is vehicle */}
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.type !== curr.type}>
              {({ getFieldValue }) => {
                const type = getFieldValue("type");
                if (type === "vehicle") {
                  return (
                    <div className="p-4 bg-orange-50/50 rounded-lg border border-orange-100 mb-4">
                      <p className="text-xs text-orange-600 font-semibold mb-2">
                        📍 Initial GPS Coordinates (Optional)
                      </p>
                      <div className="flex gap-2">
                        <Form.Item
                          name="latitude"
                          label="Latitude"
                          className="mb-0 flex-1"
                          rules={[
                            {
                              pattern: /^-?\d+(\.\d+)?$/,
                              message: "Invalid decimal number",
                            },
                          ]}
                        >
                          <Input placeholder="6.9271" />
                        </Form.Item>
                        <Form.Item
                          name="longitude"
                          label="Longitude"
                          className="mb-0 flex-1"
                          rules={[
                            {
                              pattern: /^-?\d+(\.\d+)?$/,
                              message: "Invalid decimal number",
                            },
                          ]}
                        >
                          <Input placeholder="79.8612" />
                        </Form.Item>
                      </div>
                      <span className="text-[10px] text-gray-400 mt-1 block">
                        Leave empty to auto-initialize or set later via tracking.
                      </span>
                    </div>
                  );
                }
                return null;
              }}
            </Form.Item>

            <Form.Item name="status" label="Registry Status">
              <Select
                options={[
                  { value: "Active", label: "Active" },
                  { value: "Inactive", label: "Inactive" },
                ]}
              />
            </Form.Item>

            <Form.Item name="main_store" label="Designate as Main Store" valuePropName="checked">
              <Switch disabled={!isCreating && editingStore?.main_store === 1} />
            </Form.Item>
          </Form>
        </Spin>
      </Drawer>


    </div>
  );
};
